import { useMemo } from "react";
import { Chip, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { isRootCauseWorkspace } from "../..";
import AdvancedFiltersModal from "../../../../../components/common/AdvanceFilterModal";
import { useFiltersRegistry } from "../../../../../context/FiltersProvider";
import { getRootCauseDashboardFilters } from "../../../../../context/FiltersProvider/selectors/RootCause/dashboardSelectors";
import { useNavigationPreferences } from "../../../../../hooks/usePreferences";
import { useTransportFilterContext } from "../../../../../hooks/useTransportFilterContext ";
import { RootCauseCardType } from "../../data";
import { DashboardAccordion } from "../../RootCauseCard/components/CardDashboard/components/DashboardAccordion";

type Props = {
	cardType: RootCauseCardType;
};

export const CardDashboardReadonly = ({ cardType }: Props) => {
	const { t } = useTranslation("dataInsights.rootcause");
	const { navigationPreferences } = useNavigationPreferences();
	const { registry, rootCauseState } = useFiltersRegistry();

	const filterContext = useTransportFilterContext(navigationPreferences.transportsPerspective);

	const workspace = isRootCauseWorkspace(cardType) ? rootCauseState[cardType] : null;

	const filters = useMemo(() => {
		if (!workspace) return [];

		return getRootCauseDashboardFilters(registry, workspace.dashboardFilters);
	}, [registry, workspace]);

	if (cardType === "perimeter") {
		return (
			<DashboardAccordion title={t("cardSubTitles/criteria")} count={rootCauseState.perimeter.length} readonly>
				<Stack direction="row" spacing={2} flexWrap="wrap">
					{rootCauseState.perimeter.length === 0 ? (
						<Typography variant="body2" color="text.secondary">
							{t(`emptyDashboard/${cardType}/no filters`)}
						</Typography>
					) : (
						rootCauseState.perimeter.map((filter) => <Chip disabled key={filter.id} label={filter.label} />)
					)}
				</Stack>
			</DashboardAccordion>
		);
	}

	if (!workspace) {
		return null;
	}

	const count = workspace.advancedMode ? workspace.advancedForm.length : filters.length;

	return count > 0 ? (
		<DashboardAccordion title={t("cardSubTitles/condition")} count={count} readonly>
			{!workspace.advancedMode && (
				<Stack direction="row" flexWrap="wrap" gap={2}>
					{filters.map(({ instance, filter }) => {
						const values = Array.isArray(instance.value)
							? instance.value
							: instance.value
								? [instance.value]
								: [];

						const labels = values.map((value) => {
							const option = filter.field?.options?.find((opt) => String(opt.id) === String(value));

							return option?.label ?? value;
						});

						return (
							<Chip
								disabled
								key={instance.id}
								label={
									<Stack direction="row" spacing={1} alignItems="center">
										<span>
											{filter.name}
											{instance.operator ? ` ${instance.operator}` : ""}
										</span>

										{labels.length > 0 && (
											<Chip
												color="primary"
												label={labels.join(", ")}
												size="small"
												variant="filled"
											/>
										)}
									</Stack>
								}
							/>
						);
					})}
				</Stack>
			)}

			{workspace.advancedMode && (
				<AdvancedFiltersModal
					filterContext={filterContext}
					items={workspace.advancedForm}
					dispatch={() => {}}
					readOnly
				/>
			)}
		</DashboardAccordion>
	) : (
		<Typography variant="body2" color="text.secondary">
			{t(`emptyDashboard/${cardType}/no filters`)}
		</Typography>
	);
};
